import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, MagnifyingGlass, Package, PencilSimple, Trash, UserSwitch, Recycle, Cube, Warning,
  ChartBar, Table as TableIcon, Minus, PaperPlaneTilt,
  Printer, Tag, MapPin, UsersThree, DotsThreeVertical, SlidersHorizontal, Sparkle, QrCode, ClipboardText,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { BarcodeScanDialog } from '@/pages/docs/BarcodeDocs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAuth, canEditIt } from '@/auth/AuthContext';
import useDirectory from '@/hooks/useDirectory';
import { itApi, ASSET_STATUS_META, OWNERSHIP_META } from './itApi';
import { openPrintWindow } from '@/lib/printWindow';
import { buildAssetCostReportHtml } from './assetCostReport';
import AssetFormDialog from './AssetFormDialog';
import AiAssetImportModal from '@/components/aiimport/AiAssetImportModal';
import AssetDetailDialog from './AssetDetailDialog';
import AssetDashboard from './AssetDashboard';
import DisposalDialog from './DisposalDialog';
import ConsumableReceiptDialog from './ConsumableReceiptDialog';
import LabelPrintDialog from '@/components/labels/LabelPrintDialog';
import { buildAssetLabel } from '@/components/labels/labelSizes';
import { DataTable } from '@/components/ui/DataTable';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { CategoryChip } from './categoryColor';
import { durableCols, consumableCols } from './assetColumns';
import AssetColumnsDialog from './AssetColumnsDialog';

const yen = (n) => (typeof n === 'number' ? `¥${n.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}` : '—');

const StatCard = ({ label, value, sub, testid, accent }) => (
  <div data-testid={testid} className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4 transition-transform hover:-translate-y-0.5">
    <p className="text-xs text-zinc-500">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${accent || 'text-zinc-900'}`}>{value}</p>
    {sub && <p className="text-[11px] text-zinc-400 mt-0.5">{sub}</p>}
  </div>
);

function UserAssetView() {
  const [assets, setAssets] = useState([]);
  useEffect(() => { itApi.assets().then(setAssets).catch(() => {}); }, []);
  return (
    <div className="space-y-5" data-testid="asset-management">
      <h1 className="text-2xl font-bold tracking-tight text-zinc-900">自分の備品</h1>
      <p className="text-sm text-zinc-500 -mt-3">自分に割り当てられた備品と、所属部署の共用備品を表示します</p>
      {assets.length === 0 ? (
        <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center" data-testid="asset-user-empty">
          <Package size={40} weight="duotone" className="text-zinc-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-zinc-700">割り当てられた備品はありません</p>
          <p className="text-xs text-zinc-400 mt-1">備品が必要な場合は申請センターから申請してください</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="asset-user-grid">
          {assets.map((a) => (
            <div key={a.id} className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4" data-testid={`asset-card-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-zinc-900 truncate" data-no-translate>{a.name}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{a.asset_tag || '—'}</p>
                  {a.is_shared && (
                    <Badge variant="outline" className="mt-1 bg-teal-50 text-teal-700 border-teal-200 text-[10px]" data-testid={`asset-shared-badge-${a.id}`}>
                      <UsersThree size={10} weight="bold" className="mr-1" /> 部署共用{a.assigned_to_department_name ? `: ${a.assigned_to_department_name}` : ''}
                    </Badge>
                  )}
                </div>
                <Cube size={22} weight="duotone" className="text-teal-700 shrink-0" />
              </div>
              <div className="mt-3 text-xs text-zinc-500 space-y-1">
                <p className="flex items-center gap-1.5">カテゴリ: {a.category ? <CategoryChip category={a.category} /> : '—'}</p>
                <p>メーカー/型番: <span data-no-translate>{[a.maker, a.model].filter(Boolean).join(' / ') || '—'}</span></p>
                <p>シリアル: <span data-no-translate>{a.serial || '—'}</span></p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AssetManagement() {
  const { user } = useAuth();
  const admin = canEditIt(user);
  if (!admin) return <UserAssetView />;
  return <AdminAssetView />;
}

function AdminAssetView() {
  // U5 — ドキュメントのメニューから移設。ラベルを読んで、その資産の関連書類を開く。
  // ここで宣言する（以前は外側の AssetManagement にあり、この画面から参照できず
  // `labelScanOpen is not defined` で真っ白になっていた）。
  const [labelScanOpen, setLabelScanOpen] = useState(false);
  const navigate = useNavigate();
  const { employees, departments } = useDirectory();
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [assetType, setAssetType] = useState('durable'); // 'durable' | 'consumable'
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [needsInputOnly, setNeedsInputOnly] = useState(false);
  // 「要入力 N件」バナー（読み取り専用の点検サマリー）
  const [needsInput, setNeedsInput] = useState(null);
  const [locationFilter, setLocationFilter] = useState(null);
  const [managedLocs, setManagedLocs] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // QRラベルのスキャン → ?open=<id> で詳細を直接開く
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      setDetailId(openId);
      setSearchParams((p) => { p.delete('open'); return p; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selected, setSelected] = useState(() => new Set());
  const [view, setView] = useState('list');
  const [disposeAsset, setDisposeAsset] = useState(null);
  const [receiptAsset, setReceiptAsset] = useState(null);
  // Bulk label print state — hold the array of built labels; dialog opens when non-null.
  const [labelsToPrint, setLabelsToPrint] = useState(null);
  // Column preferences (durable / consumable each persisted in localStorage)
  const [durableCfg, setDurableCfg] = useState(durableCols.load);
  const [consumableCfg, setConsumableCfg] = useState(consumableCols.load);
  const [colsDialogOpen, setColsDialogOpen] = useState(false);
  useEffect(() => { durableCols.save(durableCfg); }, [durableCfg]);
  useEffect(() => { consumableCols.save(consumableCfg); }, [consumableCfg]);
  const activeCfg = assetType === 'durable' ? durableCfg : consumableCfg;
  const setActiveCfg = assetType === 'durable' ? setDurableCfg : setConsumableCfg;
  const activeHelpers = assetType === 'durable' ? durableCols : consumableCols;
  const toggleCol = (key) => setActiveCfg((cfg) => ({ ...cfg, columns: cfg.columns.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)) }));
  const moveCol = (key, delta) => setActiveCfg((cfg) => {
    const arr = [...cfg.columns];
    const i = arr.findIndex((c) => c.key === key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= arr.length) return cfg;
    // Fixed columns cannot be moved.
    if (activeHelpers.FIXED_KEYS.has(arr[i].key) || activeHelpers.FIXED_KEYS.has(arr[j].key)) return cfg;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...cfg, columns: arr };
  });
  const resetCols = () => setActiveCfg(activeHelpers.defaultConfig());

  const load = useCallback(() => {
    const params = { asset_type: assetType };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (categoryFilter !== 'all') params.category = categoryFilter;
    if (assignedFilter !== 'all') params.assigned = assignedFilter;
    if (q.trim()) params.q = q.trim();
    if (needsInputOnly) params.needs_input = true;
    itApi.assets(params).then((rows) => {
      setAssets(lowStockOnly ? rows.filter((r) => r.low_stock) : rows);
    }).catch(() => {});
    // Stats also filtered by asset_type so the header KPIs match the visible list/dashboard
    itApi.assetStats({ asset_type: assetType }).then(setStats).catch(() => {});
  }, [statusFilter, categoryFilter, assignedFilter, q, assetType, lowStockOnly, needsInputOnly]);

  useEffect(() => {
    itApi.dataReportSummary().then(setNeedsInput).catch(() => setNeedsInput(null));
  }, []);

  useEffect(() => { load(); }, [load]);
  const [aiImportOpen, setAiImportOpen] = useState(false);
  const [bulkLoc, setBulkLoc] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => {
    itApi.assetCategories().then((d) => setCategories(d.categories || [])).catch(() => {});
    itApi.assetLocations().then((d) => setManagedLocs((d.managed || []).map((l) => l.name))).catch(() => {});
  }, []);

  const deptMap = useMemo(() => new Map((departments || []).map((d) => [d.id, d.name])), [departments]);
  // 場所別サマリー — counts from the currently loaded list; master locations first
  const locCounts = useMemo(() => {
    const m = new Map();
    assets.forEach((a) => { const k = a.location || '未設定'; m.set(k, (m.get(k) || 0) + 1); });
    const ordered = [];
    managedLocs.forEach((n) => { if (m.has(n)) { ordered.push([n, m.get(n)]); m.delete(n); } });
    const none = m.get('未設定'); m.delete('未設定');
    const rest = [...m.entries()].sort((x, y) => y[1] - x[1]);
    return [...ordered, ...rest, ...(none ? [['未設定', none]] : [])];
  }, [assets, managedLocs]);
  const visibleAssets = useMemo(
    () => (locationFilter ? assets.filter((a) => (a.location || '未設定') === locationFilter) : assets),
    [assets, locationFilter],
  );

  // 一括設定の候補 — マスタの場所 + 実際に使われている場所（「未設定」は選択肢から除く）
  const bulkLocOptions = useMemo(() => {
    const used = locCounts.map(([n]) => n).filter((n) => n !== '未設定');
    return [...new Set([...managedLocs, ...used])];
  }, [managedLocs, locCounts]);

  const applyBulkLocation = async () => {
    const loc = bulkLoc === '__unset__' ? '' : bulkLoc;
    setBulkBusy(true);
    try {
      const r = await itApi.bulkSetAssetLocation([...selected], loc);
      toast.success(`${r.updated}件の設置場所を「${loc || '未設定'}」にしました`);
      setSelected(new Set());
      setBulkLoc('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '一括設定に失敗しました');
    } finally { setBulkBusy(false); }
  };

  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allSelected = visibleAssets.length > 0 && visibleAssets.every((a) => selected.has(a.id));
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(visibleAssets.map((a) => a.id))));

  const openBulkLabelPrint = (ids) => {
    // Brother QL ラベル一括印刷 — restrict to `ids`, fallback to all visible assets.
    const rows = (ids && ids.length)
      ? assets.filter((a) => ids.includes(a.id))
      : visibleAssets;
    if (!rows.length) {
      toast.error('印刷する資産を選択してください');
      return;
    }
    const labels = rows.map((a) => buildAssetLabel(a, deptMap.get(a.assigned_to_department_id) || a.assigned_to_department_name || ''));
    setLabelsToPrint(labels);
  };

  const del = async (a) => {
    if (!window.confirm(`「${a.name}」を削除しますか？`)) return;
    try { await itApi.deleteAsset(a.id); toast.success('削除しました'); load(); }
    catch { toast.error('削除に失敗しました'); }
  };

  const adjustQty = async (a, delta) => {
    try {
      await itApi.adjustConsumable(a.id, { delta });
      toast.success(delta > 0 ? `+${delta}` : `${delta}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '調整に失敗しました');
    }
  };

  const reorderNow = async (a) => {
    if (!window.confirm(`「${a.name}」の補充依頼を作成しますか？（自動で 発注依頼 に追加されます）`)) return;
    try {
      const r = await itApi.reorderRequest(a.id);
      toast.success(`補充依頼を作成しました (${r.request_no})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '依頼作成に失敗しました');
    }
  };

  const openNew = () => { setEditAsset(null); setFormOpen(true); };
  const openEdit = (a) => { setEditAsset(a); setFormOpen(true); };

  const ViewTab = ({ value, Icon, children }) => (
    <button type="button" data-testid={`asset-view-${value}`} onClick={() => setView(value)}
      className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${
        view === value ? 'bg-white text-teal-700 shadow-sm font-semibold' : 'text-zinc-500 hover:text-zinc-800'
      }`}>
      <Icon size={16} weight={view === value ? 'bold' : 'regular'} /> {children}
    </button>
  );

  return (
    <div className="space-y-5" data-testid="asset-management">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">IT資産管理</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" data-testid="asset-label-print-btn"
            disabled={selected.size === 0 && assets.length === 0}
            onClick={() => openBulkLabelPrint(selected.size ? [...selected] : null)}
            className="h-11 rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50">
            <Printer size={18} className="mr-1.5" weight="duotone" />
            ラベル印刷{selected.size ? ` (${selected.size})` : '（全件）'}
          </Button>
          <Button variant="outline" data-testid="asset-cost-report-btn" disabled={assets.length === 0}
            onClick={() => openPrintWindow(buildAssetCostReportHtml(assets))} className="h-11 rounded-lg">
            <ChartBar size={18} className="mr-1.5" weight="duotone" /> コストレポート
          </Button>
          <Button variant="outline" data-testid="asset-label-scan-btn"
            onClick={() => setLabelScanOpen(true)} className="h-11 rounded-lg">
            <QrCode size={17} className="mr-1.5" /> 資産ラベル読取
          </Button>
          <Button data-testid="asset-ai-import-btn" variant="outline" onClick={() => setAiImportOpen(true)}
            className="h-11 rounded-lg border-blue-200 text-blue-700 hover:bg-blue-50">
            <Sparkle size={18} className="mr-1.5" weight="fill" /> AIインポート
          </Button>
          <Button asChild variant="outline" data-testid="asset-data-report-btn" className="h-11 rounded-lg">
            <Link to="/it/data-report"><ClipboardText size={17} className="mr-1.5" /> データ点検</Link>
          </Button>
          <Button data-testid="asset-new-btn" onClick={openNew} className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800 shadow-sm">
            <Plus size={18} className="mr-1.5" weight="bold" /> 新規登録
          </Button>
        </div>
      </div>

      {/* A. 必須4項目（購入日・取得価額・購入先・REQ-No.）が未入力の件数。
          既存データは書き換えていない — 点検レポートで埋める場所を示すだけ。 */}
      {needsInput && needsInput.total > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
          data-testid="asset-needs-input-banner">
          <Warning size={16} weight="fill" className="text-amber-600 shrink-0" />
          <span className="text-xs font-semibold text-amber-900">
            要入力 {needsInput.total}件
          </span>
          <span className="text-xs text-amber-800" data-testid="asset-needs-input-breakdown">
            （固定資産 {needsInput.durable}件 / 消耗品 {needsInput.consumable}件）— 購入日・取得価額・購入先・REQ-No. が未入力です
          </span>
          <Button asChild size="sm" variant="outline"
            className="h-8 border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
            data-testid="asset-needs-input-report-link">
            <Link to="/it/data-report?filter=needs_input">点検レポートを開く</Link>
          </Button>
          <button type="button"
            onClick={() => { setNeedsInputOnly(true); setView('list'); }}
            className="text-xs font-semibold text-amber-900 underline hover:no-underline"
            data-testid="asset-needs-input-filter-link">この一覧を要入力だけに絞る</button>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="asset-stats">
          <StatCard testid="asset-stat-total" label={assetType === 'consumable' ? '登録消耗品' : '登録資産'} value={stats.total} />
          {assetType === 'durable' ? (
            <>
              <StatCard testid="asset-stat-inuse" label="使用中" value={stats.by_status.in_use} accent="text-blue-600" />
              <StatCard testid="asset-stat-instock" label="在庫" value={stats.by_status.in_stock} />
              <StatCard testid="asset-stat-repair" label="修理中" value={stats.by_status.repair} accent="text-amber-600" />
              <StatCard testid="asset-stat-book" label="簿価合計" value={yen(stats.total_book_value)} sub={`取得 ${yen(stats.total_cost)}`} accent="text-teal-700" />
              <StatCard testid="asset-stat-warranty" label="保証60日以内" value={stats.warranty_expiring_soon} accent={stats.warranty_expiring_soon ? 'text-rose-600' : 'text-zinc-900'} />
            </>
          ) : (
            <>
              <StatCard testid="asset-stat-cons-onhand" label="在庫総数" value={(stats.total_on_hand_qty ?? 0).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })} accent="text-purple-600" />
              <StatCard testid="asset-stat-cons-low" label="低在庫" value={stats.low_stock_count || 0} accent={(stats.low_stock_count || 0) > 0 ? 'text-rose-600' : 'text-zinc-900'} />
              <StatCard testid="asset-stat-cons-cost" label="取得価額 合計" value={yen(stats.total_cost)} accent="text-teal-700" />
              <StatCard testid="asset-stat-cons-disposed" label="廃棄" value={stats.by_status.disposed} accent="text-zinc-500" />
            </>
          )}
        </div>
      )}

      {/* Unified tab bar — view mode (一覧/分析) + asset type (資産/消耗品). Always both visible so tabs never disappear when switching between views. */}
      <div className="flex flex-wrap items-center gap-3" data-testid="asset-tabbar">
        <div className="inline-flex items-center gap-1 rounded-lg bg-zinc-100 p-1" data-testid="asset-view-toggle">
          <ViewTab value="list" Icon={TableIcon}>一覧</ViewTab>
          <ViewTab value="dashboard" Icon={ChartBar}>分析</ViewTab>
        </div>
        <span className="hidden sm:inline-block h-6 w-px bg-zinc-200" aria-hidden="true" />
        <div className="inline-flex items-center gap-1 rounded-lg bg-zinc-100 p-1" data-testid="asset-type-toggle">
          <button type="button" onClick={() => { setAssetType('durable'); setLowStockOnly(false); setLocationFilter(null); }}
                  data-testid="asset-type-durable"
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${
                    assetType === 'durable' ? 'bg-white text-teal-700 shadow-sm font-semibold' : 'text-zinc-500 hover:text-zinc-800'
                  }`}>
            <Cube size={16} weight={assetType === 'durable' ? 'bold' : 'regular'} /> 資産
          </button>
          <button type="button" onClick={() => { setAssetType('consumable'); setLocationFilter(null); }}
                  data-testid="asset-type-consumable"
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${
                    assetType === 'consumable' ? 'bg-white text-purple-600 shadow-sm font-semibold' : 'text-zinc-500 hover:text-zinc-800'
                  }`}>
            <Package size={16} weight={assetType === 'consumable' ? 'bold' : 'regular'} /> 消耗品
          </button>
        </div>
      </div>

      {view === 'dashboard' && <AssetDashboard assetType={assetType} />}

      {view === 'list' && (<>
      {/* 選択行の一括操作 — 設置場所が未設定の資産をまとめて片付けるため */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2"
          data-testid="asset-bulk-bar">
          <span className="text-xs font-semibold text-teal-900">{selected.size}件を選択中</span>
          <span className="text-xs text-teal-800">設置場所を一括設定:</span>
          <div className="w-56">
            <SearchableSelect
              value={bulkLoc}
              onValueChange={setBulkLoc}
              placeholder="場所を選択…"
              searchPlaceholder="場所名で検索"
              testid="asset-bulk-location-select"
              options={[
                { value: '__unset__', primary: '未設定に戻す' },
                ...bulkLocOptions.map((n) => ({ value: n, primary: n })),
              ]}
            />
          </div>
          <Button size="sm" className="h-8 bg-teal-700 hover:bg-teal-800"
            disabled={!bulkLoc || bulkBusy} onClick={applyBulkLocation}
            data-testid="asset-bulk-location-apply">
            {bulkBusy ? '適用中…' : '適用'}
          </Button>
          <button type="button" onClick={() => setSelected(new Set())}
            className="text-xs text-teal-800 hover:underline" data-testid="asset-bulk-clear">選択解除</button>
        </div>
      )}

      {/* 場所別サマリー — chips with counts; click to filter */}
      {locCounts.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="asset-location-summary">
          <MapPin size={15} weight="duotone" className="text-teal-700 shrink-0" />
          <button type="button" onClick={() => setLocationFilter(null)}
            data-testid="asset-location-chip-all"
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
              !locationFilter ? 'bg-teal-700 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
            }`}>
            すべて <span className={!locationFilter ? 'text-blue-100' : 'text-zinc-400'}>{assets.length}</span>
          </button>
          {locCounts.map(([loc, count]) => (
            <button key={loc} type="button" onClick={() => setLocationFilter(locationFilter === loc ? null : loc)}
              data-testid={`asset-location-chip-${loc}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                locationFilter === loc ? 'bg-teal-700 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}>
              {loc} <span className={locationFilter === loc ? 'text-blue-100' : 'text-zinc-400'}>{count}</span>
            </button>
          ))}
        </div>
      )}
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="asset-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="品名・資産番号・シリアル・使用者で検索" className="h-10 pl-9 rounded-lg" />
        </div>
        {assetType === 'durable' && (
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="asset-status-filter" className="h-10 w-36 rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">状態: すべて</SelectItem>
              <SelectItem value="in_stock">在庫</SelectItem>
              <SelectItem value="in_use">使用中</SelectItem>
              <SelectItem value="repair">修理中</SelectItem>
              <SelectItem value="disposed">廃棄</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger data-testid="asset-category-filter" className="h-10 w-44 rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">カテゴリ: すべて</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        {assetType === 'durable' && (
          <Select value={assignedFilter} onValueChange={setAssignedFilter}>
            <SelectTrigger data-testid="asset-assigned-filter" className="h-10 w-36 rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">割当: すべて</SelectItem>
              <SelectItem value="assigned">割当済み</SelectItem>
              <SelectItem value="shared">部署共用</SelectItem>
              <SelectItem value="unassigned">未割当</SelectItem>
            </SelectContent>
          </Select>
        )}
        {assetType === 'consumable' && (
          <button
            type="button" onClick={() => setLowStockOnly((v) => !v)}
            data-testid="asset-lowstock-filter"
            className={`h-10 rounded-lg px-3 text-sm inline-flex items-center gap-1.5 border transition-colors ${
              lowStockOnly ? 'bg-rose-50 text-rose-700 border-rose-200 font-semibold' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'
            }`}
          >
            <Warning size={14} weight="bold" /> 低在庫のみ
          </button>
        )}
        <button type="button" onClick={() => setNeedsInputOnly((v) => !v)}
          data-testid="asset-needs-input-filter"
          title="購入日・取得価額・購入先・REQ-No. が未入力のものだけ"
          className={`h-10 rounded-lg px-3 text-sm inline-flex items-center gap-1.5 border transition-colors ${
            needsInputOnly ? 'bg-amber-50 text-amber-800 border-amber-300 font-semibold' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'
          }`}>
          <Warning size={14} weight="bold" /> 要入力のみ
        </button>
        <button type="button" onClick={() => setColsDialogOpen(true)}
          data-testid="asset-cols-btn"
          className="h-10 rounded-lg px-3 text-sm inline-flex items-center gap-1.5 border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 hover:text-zinc-800 transition-colors"
          title="列の表示・並び替え">
          <SlidersHorizontal size={14} weight="bold" /> 列設定
        </button>
      </div>

      {/* Table — DataTable renders cards on mobile so nothing squishes */}
      {assetType === 'durable' ? (
        <DataTable
          dataTestId="asset-table"
          rows={visibleAssets}
          rowKey={(a) => a.id}
          rowTestId={(a) => `asset-row-${a.id}`}
          empty={{ icon: <Package size={40} className="text-zinc-300 mb-3" weight="duotone" />, text: '資産がありません', testid: 'asset-empty' }}
          columns={(() => {
            const map = {
              asset_tag: { key: 'asset_tag', header: '資産番号', width: 'w-36',
                cell: (a) => <span className="text-zinc-500 whitespace-nowrap font-mono text-xs truncate block max-w-[8rem]" title={a.asset_tag || ''}>{a.asset_tag || '—'}</span> },
              name: { key: 'name', header: '品名', primary: true,
                cell: (a) => (
                  <span className="text-zinc-800 font-medium">
                    <span data-no-translate>{a.name}</span>
                    {a.needs_input && (
                      <Badge variant="outline" className="ml-1.5 border-amber-300 bg-amber-50 text-[9px] px-1 py-0 align-middle text-amber-800"
                        title={`未入力: ${(a.missing_required || []).join(' / ')}`}
                        data-testid={`asset-needs-input-${a.id}`}>要入力</Badge>
                    )}
                    {a.ownership && a.ownership !== 'owned' && (
                      <Badge variant="outline" className={`ml-1.5 text-[9px] px-1 py-0 align-middle ${OWNERSHIP_META[a.ownership]?.cls || ''}`} data-testid={`asset-ownership-${a.id}`}>
                        {OWNERSHIP_META[a.ownership]?.label}
                      </Badge>
                    )}
                    {(a.maker || a.model) && <span className="block text-xs text-zinc-400 font-normal" data-no-translate>{[a.maker, a.model].filter(Boolean).join(' ')}</span>}
                  </span>
                ) },
              category: { key: 'category', header: 'カテゴリ', cell: (a) => <CategoryChip category={a.category} /> },
              status: { key: 'status', header: '状態', mobileBadge: true, sortable: false,
                cell: (a) => {
                  const sm = ASSET_STATUS_META[a.status] || ASSET_STATUS_META.in_stock;
                  const warn = a.warranty_until && a.status !== 'disposed' && (new Date(a.warranty_until) - new Date()) / 86400000 <= 60;
                  return (
                    <span className="whitespace-nowrap">
                      <Badge variant="outline" className={sm.cls}>{sm.label}</Badge>
                      {warn && <Warning size={14} weight="fill" className="inline ml-1.5 text-rose-500" title="保証期限間近" />}
                    </span>
                  );
                } },
              assigned_to_name: { key: 'assigned_to_name', header: '使用者', width: 'w-32',
                cell: (a) => a.assigned_to_name
                  ? <span className="text-zinc-600 whitespace-nowrap" data-no-translate>{a.assigned_to_name}</span>
                  : a.is_shared
                    ? (
                      <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 text-[10px] whitespace-nowrap" data-testid={`asset-shared-${a.id}`}>
                        <UsersThree size={10} weight="bold" className="mr-1" />
                        共用{(deptMap.get(a.assigned_to_department_id) || a.assigned_to_department_name) ? `: ${deptMap.get(a.assigned_to_department_id) || a.assigned_to_department_name}` : ''}
                      </Badge>
                    )
                    : <span className="text-zinc-300 whitespace-nowrap">未割当</span> },
              book_value: { key: 'book_value', header: '簿価', align: 'right', sortValue: (a) => a.book_value ?? -1,
                cell: (a) => <span className="whitespace-nowrap text-zinc-700 tabular-nums">{yen(a.book_value)}</span> },
            };
            const select = { key: 'select', width: 'w-10', sortable: false, mobileHidden: true,
              header: (<Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="asset-select-all" aria-label="すべて選択" />),
              cell: (a) => <Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggleRow(a.id)} data-testid={`asset-select-${a.id}`} aria-label="選択" /> };
            const actions = { key: 'actions', header: '操作', actions: true, width: 'w-40',
              cell: (a) => (
                <>
                  <Button size="sm" variant="outline" data-testid={`asset-detail-${a.id}`} onClick={() => setDetailId(a.id)} className="h-8 px-2 text-teal-700 border-blue-200 hover:bg-blue-50 whitespace-nowrap text-xs shrink-0" title="詳細を表示・使用者を割当">
                    <UserSwitch size={14} weight="bold" className="mr-1" /> 詳細・割当
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" data-testid={`asset-more-${a.id}`} className="h-8 w-8 p-0 text-zinc-500 hover:text-zinc-800" title="その他の操作">
                        <DotsThreeVertical size={18} weight="bold" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem data-testid={`asset-edit-${a.id}`} onClick={() => openEdit(a)} className="cursor-pointer">
                        <PencilSimple size={14} className="mr-2" /> 編集
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`asset-label-${a.id}`} onClick={() => openBulkLabelPrint([a.id])} className="cursor-pointer">
                        <Tag size={14} className="mr-2" /> ラベル印刷
                      </DropdownMenuItem>
                      {a.status !== 'disposed' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem data-testid={`asset-dispose-${a.id}`} onClick={() => setDisposeAsset(a)} className="cursor-pointer text-amber-700 focus:text-amber-800 focus:bg-amber-50">
                            <Recycle size={14} className="mr-2" /> 廃棄処理
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuItem data-testid={`asset-delete-${a.id}`} onClick={() => del(a)} className="cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50">
                        <Trash size={14} className="mr-2" /> 削除
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) };
            return [select, ...durableCfg.columns.filter((c) => c.visible).map((c) => map[c.key]).filter(Boolean), actions];
          })()}
        />
      ) : (
        <DataTable
          dataTestId="consumable-table"
          rows={visibleAssets}
          rowKey={(a) => a.id}
          rowTestId={(a) => `consumable-row-${a.id}`}
          empty={{ icon: <Package size={40} className="text-zinc-300 mb-3" weight="duotone" />, text: '消耗品がありません', testid: 'consumable-empty' }}
          columns={(() => {
            const map = {
              name: { key: 'name', header: '品名', primary: true,
                cell: (a) => (
                  <span className="text-zinc-800 font-medium">
                    <span data-no-translate>{a.name}</span>
                    {a.needs_input && (
                      <Badge variant="outline" className="ml-1.5 border-amber-300 bg-amber-50 text-[9px] px-1 py-0 align-middle text-amber-800"
                        title={`未入力: ${(a.missing_required || []).join(' / ')}`}
                        data-testid={`asset-needs-input-${a.id}`}>要入力</Badge>
                    )}
                    {a.ownership && a.ownership !== 'owned' && (
                      <Badge variant="outline" className={`ml-1.5 text-[9px] px-1 py-0 align-middle ${OWNERSHIP_META[a.ownership]?.cls || ''}`} data-testid={`asset-ownership-${a.id}`}>
                        {OWNERSHIP_META[a.ownership]?.label}
                      </Badge>
                    )}
                    {(a.maker || a.model) && <span className="block text-xs text-zinc-400 font-normal" data-no-translate>{[a.maker, a.model].filter(Boolean).join(' ')}</span>}
                  </span>
                ) },
              category: { key: 'category', header: 'カテゴリ', cell: (a) => <CategoryChip category={a.category} /> },
              location: { key: 'location', header: '保管場所', cell: (a) => a.location ? <span className="text-zinc-600">{a.location}</span> : <span className="text-zinc-300">—</span> },
              on_hand_qty: { key: 'on_hand_qty', header: '在庫', align: 'right', sortValue: (a) => a.on_hand_qty ?? 0,
                cell: (a) => (
                  <div className="inline-flex items-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => adjustQty(a, -1)} data-testid={`consumable-decr-${a.id}`}>
                      <Minus size={12} weight="bold" />
                    </Button>
                    <span className="tabular-nums font-semibold text-zinc-800 min-w-[3rem] text-center" data-testid={`consumable-qty-${a.id}`}>
                      {(a.on_hand_qty ?? 0).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                      <span className="text-xs text-zinc-400 ml-1">{a.unit || '個'}</span>
                      {a.pack_label && (
                        <span className="block text-[10px] font-normal text-purple-700" data-testid={`consumable-pack-${a.id}`}>
                          {a.pack_label}
                        </span>
                      )}
                    </span>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => adjustQty(a, 1)} data-testid={`consumable-incr-${a.id}`}>
                      <Plus size={12} weight="bold" />
                    </Button>
                  </div>
                ) },
              reorder_point: { key: 'reorder_point', header: '発注点', align: 'right',
                cell: (a) => a.reorder_point != null ? <span className="tabular-nums text-zinc-600">{a.reorder_point} {a.unit || ''}</span> : <span className="text-zinc-300">—</span> },
              low_stock: { key: 'low_stock', header: '状態', mobileBadge: true, sortable: false,
                cell: (a) => a.low_stock ? (
                  <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px] whitespace-nowrap" data-testid={`consumable-low-${a.id}`}>
                    <Warning size={10} weight="bold" className="mr-1" /> 低在庫
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] whitespace-nowrap">十分</Badge>
                ) },
            };
            const actions = { key: 'actions', header: '操作', actions: true, width: 'w-36',
              cell: (a) => (
                <>
                  <Button size="sm" variant="outline" onClick={() => setReceiptAsset(a)}
                    className="h-7 px-2 text-purple-700 hover:bg-purple-50 border-purple-200 whitespace-nowrap shrink-0"
                    data-testid={`consumable-receipt-${a.id}`}>
                    <Package size={12} weight="bold" className="mr-1" /> 入庫
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reorderNow(a)} className="h-7 px-2 text-orange-700 hover:bg-orange-50 border-orange-200 whitespace-nowrap shrink-0" data-testid={`consumable-reorder-${a.id}`}>
                    <PaperPlaneTilt size={12} weight="bold" className="mr-1" /> 補充依頼
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" data-testid={`consumable-more-${a.id}`} className="h-7 w-7 p-0 text-zinc-500 hover:text-zinc-800" title="その他の操作">
                        <DotsThreeVertical size={16} weight="bold" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem data-testid={`consumable-detail-${a.id}`} onClick={() => setDetailId(a.id)} className="cursor-pointer">
                        <Package size={14} className="mr-2" /> 詳細・入庫履歴
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`consumable-edit-${a.id}`} onClick={() => openEdit(a)} className="cursor-pointer">
                        <PencilSimple size={14} className="mr-2" /> 編集
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`consumable-dispose-${a.id}`} onClick={() => setDisposeAsset(a)} className="cursor-pointer text-amber-700 focus:text-amber-800 focus:bg-amber-50">
                        <Recycle size={14} className="mr-2" /> 廃棄処理
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) };
            return [...consumableCfg.columns.filter((c) => c.visible).map((c) => map[c.key]).filter(Boolean), actions];
          })()}
        />
      )}
      </>)}

      <AssetFormDialog open={formOpen} onClose={() => setFormOpen(false)} asset={editAsset} categories={categories} defaultAssetType={assetType} onSaved={load} />
      <AiAssetImportModal module="it" open={aiImportOpen} onClose={() => setAiImportOpen(false)} onImported={load} />
      <AssetDetailDialog open={!!detailId} onClose={() => setDetailId(null)} assetId={detailId} employees={employees} canManage onChanged={load} />
      <DisposalDialog open={!!disposeAsset} asset={disposeAsset} onClose={() => setDisposeAsset(null)} onDisposed={load} />
      <ConsumableReceiptDialog open={!!receiptAsset} asset={receiptAsset}
        onClose={() => setReceiptAsset(null)} onSaved={load} />
      <LabelPrintDialog open={!!labelsToPrint} onClose={() => setLabelsToPrint(null)} labels={labelsToPrint || []} title="資産ラベル一括印刷" />
      <AssetColumnsDialog open={colsDialogOpen} onClose={() => setColsDialogOpen(false)}
        config={activeCfg} onToggle={toggleCol} onMove={moveCol} onReset={resetCols}
        helpers={activeHelpers}
        title={assetType === 'durable' ? '資産テーブルの列' : '消耗品テーブルの列'} />
      {labelScanOpen && (
        <BarcodeScanDialog open={labelScanOpen} onClose={() => setLabelScanOpen(false)}
          onResolved={(t) => {
            setLabelScanOpen(false);
            navigate(`/documents?view=assetdocs&asset_type=${t.ttype}&asset_id=${t.tid}`);
          }} />
      )}
    </div>
  );
}
